import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getCachedSummary } from '@/services/summaryService';
import { getGoalsSummary } from '@/services/goalsService';
import { getShoppingLists, LIST_STATUS } from '@/services/shoppingListService';
import { formatCurrency } from '@/lib/utils';

const DEFAULT_SUGGESTIONS = [
  'Confirm the essentials on your active list',
  'Log your latest receipt to keep totals accurate',
  'Peek at savings progress before the weekend'
];

const GBP = 'GBP';

export default function useCommandCenterData(userId) {
  const isMounted = useRef(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [goals, setGoals] = useState(null);
  const [lists, setLists] = useState([]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchData = useCallback(async (uid) => {
    if (!uid) {
      if (isMounted.current) {
        setSummary(null);
        setGoals(null);
        setLists([]);
        setLoading(false);
        setError(null);
      }
      return;
    }

    if (isMounted.current) {
      setLoading(true);
      setError(null);
    }

    try {
      const [summaryData, goalsData, listsResponse] = await Promise.all([
        getCachedSummary(uid),
        getGoalsSummary(uid),
        getShoppingLists(uid)
      ]);

      const listData = listsResponse?.lists || [];

      if (isMounted.current) {
        setSummary(summaryData || null);
        setGoals(goalsData || null);
        setLists(listData);
      }
    } catch (err) {
      if (isMounted.current) {
        const message = err?.message || 'Unable to load command center data';
        setError(message);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchData(userId);
  }, [userId, fetchData]);

  const refresh = useCallback(() => {
    fetchData(userId);
  }, [fetchData, userId]);

  const derived = useMemo(() => {
    const activeLists = lists.filter((list) => list.status === LIST_STATUS.ACTIVE);
    const readyCount = activeLists.filter((list) => (list.itemCount || 0) > 0).length;
    const needsItemsCount = activeLists.filter((list) => {
      const total = list.itemCount || 0;
      const purchased = list.purchasedCount || 0;
      return total > purchased;
    }).length;
    const budgetAlertCount = activeLists.filter((list) => {
      if (!list.budget || !list.totalSpent) return false;
      return list.totalSpent >= list.budget * 0.8;
    }).length;

    const listStats = {
      activeCount: activeLists.length,
      readyCount,
      needsItemsCount,
      budgetAlertCount,
      readyLabel: readyCount ? `${readyCount} active` : 'No active lists',
      helper: activeLists.length === 0
        ? 'Start a shared list'
        : budgetAlertCount
          ? `${budgetAlertCount} near budget`
          : 'Budgets look healthy'
    };

    const totalGoals = goals?.totalGoals || 0;
    const completedGoals = goals?.completedGoals || 0;
    const overallProgress = goals?.overallProgress || 0;
    const monthlyCommitment = goals?.monthlyCommitment || 0;

    const goalStats = {
      totalGoals,
      completedGoals,
      summaryLabel: totalGoals ? `${Math.round(overallProgress)}% funded` : 'No goals yet',
      helper: monthlyCommitment
        ? `${formatCurrency(monthlyCommitment, GBP)} / mo committed`
        : 'Add a savings target',
      completedLabel: totalGoals ? `${completedGoals}/${totalGoals} complete` : 'Set your first goal'
    };

    const financeStats = summary
      ? {
          hasSummary: true,
          balanceLabel: formatCurrency(summary.totalBalance || 0, GBP),
          flowHelper: `${formatCurrency(summary.totalIncome || 0, GBP)} in / ${formatCurrency(summary.totalExpenses || 0, GBP)} out`,
          savingsRateLabel: typeof summary.savingsRate === 'number'
            ? `${Number(summary.savingsRate).toFixed(0)}%`
            : '0%',
          transactionCount: summary.transactionCount || 0
        }
      : {
          hasSummary: false,
          balanceLabel: formatCurrency(0, GBP),
          flowHelper: 'Track at least one transaction',
          savingsRateLabel: '0%',
          transactionCount: 0
        };

    const checklistSet = new Set();
    if (budgetAlertCount > 0) {
      checklistSet.add(`Review ${budgetAlertCount} list${budgetAlertCount === 1 ? '' : 's'} close to its budget cap.`);
    }
    if (needsItemsCount > 0) {
      checklistSet.add(`${needsItemsCount} list${needsItemsCount === 1 ? ' still needs' : 's still need'} items checked off.`);
    }
    if (!financeStats.transactionCount) {
      checklistSet.add('Log a transaction to unlock live insights.');
    }
    if (!totalGoals) {
      checklistSet.add('Create a savings goal to stay accountable.');
    }

    const checklist = Array.from(checklistSet);

    for (const suggestion of DEFAULT_SUGGESTIONS) {
      if (checklist.length >= 3) break;
      if (!checklist.includes(suggestion)) {
        checklist.push(suggestion);
      }
    }

    return {
      listStats,
      goalStats,
      financeStats,
      checklist
    };
  }, [goals, lists, summary]);

  return {
    loading,
    error,
    refresh,
    ...derived
  };
}
